import React, { useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { HiExternalLink, HiLockClosed } from 'react-icons/hi';
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  Pagination,
  Icon,
  DatePicker,
  ActionsMenu,
  ActionsMenuItem,
  ActionsMenuDivider,
  showToast,
  Input,
  Select,
} from '@repo/ui';
import ColumnVisibilityModal from './ColumnVisibilityModal';
import { EmailSendConfirmationModal } from '@xtnd/web/features/juniper/components/EmailSendConfirmationModal';
import { ProcessingActionBadge, BulkJobProgressTracker } from '@xtnd/web/features/juniper/components';
import { usePreviewRecipients } from '@xtnd/web/features/juniper/hooks/usePreviewRecipients';
import { BulkSimulateModal } from './BulkSimulateModal';
import {
  useTrustRequestTransactions,
  useReleaseEmailMutation,
  useResendEmailMutation,
  useSendEmailMutation,
  useRegeneratePdfMutation,
  useBulkTrrReleaseMutation,
  useBulkTrrResendMutation,
  useBulkTrrRegenerateMutation,
  useBulkTrrRefreshMutation,
  useBulkTrrSimulateMutation,
  useBulkTrrMarkCompletedMutation,
  useMarkCompletedMutation as useTrrMarkCompletedMutation,
} from '../queries';
import { useClientsForFiltering } from '../queries';
import { useUsersQuery } from '@xtnd/web/shared/hooks/useUsersQuery';
import { formatCurrency, formatDate, DateFormat } from '@xtnd/web/shared/utils/format';
import { DateFormat as dateUtilDateFormat } from '@xtnd/web/shared/utils/format';
import type { TrustRequestTransaction } from '../types/trustRequestTransactionTypes';

export function TrustRequestsTransactionsView() {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Date range state
  const getDefaultDateRange = () => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);
    return {
      startDate: thirtyDaysAgo.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0],
    };
  };

  const [startDate, setStartDate] = useState(() => {
    const urlStartDate = searchParams.get('startDate');
    return urlStartDate || getDefaultDateRange().startDate;
  });

  const [endDate, setEndDate] = useState(() => {
    const urlEndDate = searchParams.get('endDate');
    return urlEndDate || getDefaultDateRange().endDate;
  });

  // Filter state
  const [matterSearchQuery, setMatterSearchQuery] = useState('');
  const [selectedClientIds, setSelectedClientIds] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedCreatedBy, setSelectedCreatedBy] = useState<string>('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  
  // Selection state
  const [selectedTransactions, setSelectedTransactions] = useState<Set<string>>(new Set());
  const [isBulkSimulateModalOpen, setBulkSimulateModalOpen] = useState(false);
  
  // Action menu state
  const [actionsMenuOpen, setActionsMenuOpen] = useState<string | null>(null);
  const [menuTriggerElement, setMenuTriggerElement] = useState<HTMLElement | null>(null);

  // Email confirmation modal state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState<TrustRequestTransaction | null>(null);
  const [confirmBulkMode, setConfirmBulkMode] = useState<'release' | 'resend' | null>(null);
  const [bulkEligibleIds, setBulkEligibleIds] = useState<string[]>([]);
  const previewRecipients = usePreviewRecipients();

  // Collapsible filters state
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(false);

  // Column visibility state - Trust Request defaults: Matter, Status, Last Sent, Created, Payment Link, Number, Status, Issued At, Total
  const [isColumnModalOpen, setIsColumnModalOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>({
    // Basic Information columns (toggleable)
    client: false,        // Client (hidden by default)
    createdAt: true,      // Created ✓
    createdBy: false,     // Created By (hidden by default)
    paymentLink: true,    // Payment Link ✓
    // Trust Request Bill columns (toggleable)
    billStatus: true,     // Status ✓
    issueAt: true,        // Issued At ✓
    dueAt: false,         // Due At (hidden by default)
    total: true,          // Total ✓
    paid: false,          // Paid (hidden by default)
    pending: false,       // Pending (hidden by default)
    trustRequestEmailType: false, // Email Type (hidden by default)
  });

  // Helper functions for filter summaries
  const getSelectedFiltersSummary = (): string => {
    const clientCount = selectedClientIds.length;
    const statusCount = selectedStatuses.length;
    const createdByCount = selectedCreatedBy ? 1 : 0;
    const totalSelected = clientCount + statusCount + createdByCount;

    if (totalSelected === 0) return 'No filters selected';

    const parts = [];
    if (clientCount > 0) parts.push(`${clientCount} client${clientCount === 1 ? '' : 's'}`);
    if (statusCount > 0) parts.push(`${statusCount} status${statusCount === 1 ? '' : 'es'}`);
    if (createdByCount > 0) parts.push('created by');

    return `${parts.join(', ')} filter${totalSelected === 1 ? '' : 's'} selected`;
  };

  // Column visibility helper functions
  const getVisibleColumnCount = (): number => {
    return Object.values(visibleColumns).filter(Boolean).length;
  };

  const getTotalColumnCount = (): number => {
    return Object.keys(visibleColumns).length;
  };

  const handleToggleColumn = (key: string) => {
    setVisibleColumns(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleToggleAllColumns = (checked: boolean) => {
    const newVisibility = Object.keys(visibleColumns).reduce((acc, key) => {
      acc[key] = checked;
      return acc;
    }, {} as Record<string, boolean>);
    setVisibleColumns(newVisibility);
  };

  // Calculate header spans for section headers
  const getHeaderSpans = () => {
    // Basic Information section (always includes: matter, status, lastSent)
    let basicInfoCols = 3; // Base columns always visible
    if (visibleColumns.client) basicInfoCols++;
    if (visibleColumns.createdAt) basicInfoCols++;
    if (visibleColumns.createdBy) basicInfoCols++;
    if (visibleColumns.paymentLink) basicInfoCols++;

  // Trust Request Bill section (always includes: number)
  let trustRequestBillCols = 1; // Base columns always visible
  if (visibleColumns.billStatus) trustRequestBillCols++;
  if (visibleColumns.issueAt) trustRequestBillCols++;
  if (visibleColumns.dueAt) trustRequestBillCols++;
  if (visibleColumns.total) trustRequestBillCols++;
  if (visibleColumns.paid) trustRequestBillCols++;
  if (visibleColumns.pending) trustRequestBillCols++;
  if (visibleColumns.trustRequestEmailType) trustRequestBillCols++;

    return {
      basicInfo: basicInfoCols,
      trustRequestBill: trustRequestBillCols,
      total: basicInfoCols + trustRequestBillCols + 2, // +2 for checkbox and actions columns
    };
  };

  // Email confirmation modal functions
  const openConfirmForTransaction = async (transaction: TrustRequestTransaction) => {
    setConfirmTarget(transaction);
    setConfirmOpen(true);
    await previewRecipients.loadRecipients(transaction.juniperTRRId, transaction.matterId);
  };

  const getDateRangeSummary = (): string => {
    if (!startDate && !endDate) return 'All dates';
    const start = startDate ? new Date(startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
    const end = endDate ? new Date(endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
    return start && end ? `${start} - ${end}` : start || end || 'All dates';
  };

  // Fetch data
  const { data: transactionsData, isLoading: isLoadingTransactions, isFetching: isFetchingTransactions, refetch: refetchTransactions } = useTrustRequestTransactions({
    dateFrom: startDate,
    dateTo: endDate,
    matterId: matterSearchQuery || undefined,
    clientIds: selectedClientIds.length > 0 ? selectedClientIds : undefined,
    trustRequestState: selectedStatuses.length > 0 ? selectedStatuses : undefined,
    createdBy: selectedCreatedBy || undefined,
    page: currentPage,
    limit: pageSize,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  const { data: clientsData } = useClientsForFiltering({ hasJuniperEnabled: true });
  const { data: usersData, isLoading: isLoadingUsers } = useUsersQuery();

  // Email mutations
  const releaseEmailMutation = useReleaseEmailMutation();
  const resendEmailMutation = useResendEmailMutation();
  const sendEmailMutation = useSendEmailMutation();
  const regeneratePdfMutation = useRegeneratePdfMutation();
  const bulkReleaseMutation = useBulkTrrReleaseMutation();
  const bulkResendMutation = useBulkTrrResendMutation();
  const bulkRegenerateMutation = useBulkTrrRegenerateMutation();
  const bulkRefreshMutation = useBulkTrrRefreshMutation();
  const bulkSimulateMutation = useBulkTrrSimulateMutation();
  const markCompletedMutation = useTrrMarkCompletedMutation();
  const bulkMarkCompletedMutation = useBulkTrrMarkCompletedMutation();

  // Active TRR bulk jobs we're polling. See the matching pattern in
  // JuniperTransactionsPage.tsx for rationale + budget.
  const [activeBulkJobs, setActiveBulkJobs] = useState<string[]>([]);
  const trackBulkJob = useCallback((resp: any) => {
    if (resp?.jobId) {
      setActiveBulkJobs((prev) => (prev.includes(resp.jobId) ? prev : [...prev, resp.jobId]));
    }
  }, []);
  const removeBulkJob = useCallback((jobId: string) => {
    setActiveBulkJobs((prev) => prev.filter((id) => id !== jobId));
  }, []);

  const handleBulkRelease = () => {
    if (!releaseEligibleTransactions.length) {
      showToast.warning('No Email Held transactions selected for release');
      return;
    }
    const ids = releaseEligibleTransactions.map(tx => tx.juniperTRRId);
    setBulkEligibleIds(ids);
    setConfirmBulkMode('release');
    setConfirmOpen(true);
  };

  const handleBulkMarkCompleted = () => {
    if (!markCompletedEligibleTransactions.length) {
      showToast.warning('No Email Held transactions selected');
      return;
    }
    bulkMarkCompletedMutation.mutate(
      { juniperTRRIds: markCompletedEligibleTransactions.map(tx => tx.juniperTRRId) },
      {
        onSuccess: (resp: any) => {
          setSelectedTransactions(new Set());
          trackBulkJob(resp);
        },
      },
    );
  };

  const handleBulkResend = () => {
    if (!resendEligibleTransactions.length) {
      showToast.warning('No Completed transactions selected for resend');
      return;
    }
    const ids = resendEligibleTransactions.map(tx => tx.juniperTRRId);
    setBulkEligibleIds(ids);
    setConfirmBulkMode('resend');
    setConfirmOpen(true);
  };

  const handleBulkRegenerate = () => {
    if (!regenerateEligibleTransactions.length) {
      showToast.warning('No Email Held transactions selected for PDF regeneration');
      return;
    }
    bulkRegenerateMutation.mutate(
      { juniperTRRIds: regenerateEligibleTransactions.map(tx => tx.juniperTRRId) },
      {
        onSuccess: (resp: any) => {
          setSelectedTransactions(new Set());
          trackBulkJob(resp);
        },
      },
    );
  };

  const handleBulkRefresh = () => {
    if (!refreshEligibleTransactions.length) {
      showToast.warning('No transactions with trust request IDs selected for refresh');
      return;
    }
    bulkRefreshMutation.mutate(
      { juniperTRRIds: refreshEligibleTransactions.map(tx => tx.juniperTRRId) },
      {
        onSuccess: (resp: any) => {
          setSelectedTransactions(new Set());
          trackBulkJob(resp);
        },
      },
    );
  };

  const openBulkSimulateModal = () => {
    if (!hasSelectedTransactions) {
      showToast.warning('Select at least one transaction to retry last events');
      return;
    }
    setBulkSimulateModalOpen(true);
  };

  const handleBulkSimulate = async (eventType: 'update' | 'delete') => {
    if (!simulateEligibleTransactions.length) {
      showToast.warning('No transactions with trust request numbers available for retry');
      return;
    }
    const resp: any = await bulkSimulateMutation.mutateAsync({
      juniperTRRIds: simulateEligibleTransactions.map(tx => tx.juniperTRRId),
      eventType,
    });
    trackBulkJob(resp);
    setSelectedTransactions(new Set());
  };

  // Transform data for Select components
  const clientOptions = useMemo(() => {
    if (!clientsData?.data) return [];
    return clientsData.data.map(client => ({
      value: client.id,
      label: client.name,
    }));
  }, [clientsData]);

  const summary = transactionsData?.summary || {
    totalRequests: 0,
    byStatus: {},
    totalAmount: 0,
    byBatch: {},
  };

  // Extract transactions from data
  const transactions = transactionsData?.data || [];

  type TransactionWithId = TrustRequestTransaction & { juniperTRRId: string };

  const selectedTransactionDetails = useMemo<TransactionWithId[]>(() => {
    const transactions = transactionsData?.data || [];
    if (!transactions.length || selectedTransactions.size === 0) {
      return [];
    }
    return transactions.filter((transaction): transaction is TransactionWithId => {
      if (!transaction.juniperTRRId) {
        return false;
      }
      return selectedTransactions.has(transaction.juniperTRRId);
    });
  }, [transactionsData?.data, selectedTransactions]);

  const releaseEligibleTransactions = useMemo(
    () =>
      selectedTransactionDetails.filter(transaction =>
        transaction.status?.toLowerCase().includes('email held')
      ),
    [selectedTransactionDetails]
  );

  const resendEligibleTransactions = useMemo(
    () =>
      selectedTransactionDetails.filter(transaction =>
        transaction.status?.toLowerCase().includes('completed')
      ),
    [selectedTransactionDetails]
  );

  const regenerateEligibleTransactions = releaseEligibleTransactions;

  const markCompletedEligibleTransactions = releaseEligibleTransactions; // Same filter: Email Held

  const refreshEligibleTransactions = useMemo(
    () => selectedTransactionDetails.filter(transaction => Boolean(transaction.trustRequestId)),
    [selectedTransactionDetails]
  );

  const simulateEligibleTransactions = useMemo(
    () =>
      selectedTransactionDetails.filter(transaction =>
        Boolean(transaction.trustRequestBill?.number || transaction.trustRequestNumber || transaction.trustRequestId)
      ),
    [selectedTransactionDetails]
  );

  const hasSelectedTransactions = selectedTransactionDetails.length > 0;

  // Transform and filter users data - only show users who have created trust request transactions
  const userNames = useMemo(() => {
    const names: Record<string, string> = {};

    // First get all users from API
    const allUsers: Record<string, string> = {};
    if (usersData) {
      Object.entries(usersData).forEach(([userId, userName]) => {
        allUsers[userId] = userName;
      });
    }

    // Get transactions data
    const transactions = transactionsData?.data || [];

    // If no transactions, return empty (no users to filter)
    if (!transactions.length) {
      return {};
    }

    // Extract unique user IDs from trust request transactions
    const userIdsFromTransactions = new Set<string>();

    transactions.forEach(transaction => {
      if (transaction.createdByUserId) {
        userIdsFromTransactions.add(transaction.createdByUserId);
      }
    });

    // Filter users to only include those found in transactions
    Object.entries(allUsers).forEach(([userId, userName]) => {
      if (userIdsFromTransactions.has(userId)) {
        names[userId] = userName;
      }
    });

    return names;
  }, [usersData, transactionsData?.data]);

  // Utility function to convert string to title case
  const toTitleCase = (str: string): string => {
    return str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
  };

  // Utility function to format raw status (consistent with Juniper Transactions page)
  const formatRawStatus = (state: string | null): string => {
    if (!state) return 'Draft';

    const statusMap: Record<string, string> = {
      not_created: 'Not Created',
      not_required: 'Not Required',
      draft: 'Draft',
      awaiting_approval: 'Awaiting Approval',
      awaiting_payment: 'Awaiting Payment',
      partial_payment: 'Partial Payment',
      pending_payment: 'Pending Payment',
      paid: 'Paid',
      void: 'Void',
      voided: 'Voided',
      deleted: 'Deleted',
      cancelled: 'Cancelled',
      approved: 'Approved',
      sent: 'Sent',
    };

    return statusMap[state.toLowerCase()] || toTitleCase(state);
  };

  // Utility function to format email type for display
  const formatEmailType = (emailType: string | undefined): string => {
    if (!emailType) return 'N/A';

    const emailTypeMap: Record<string, string> = {
      'Trust Establishment': 'Establishment',
      'Mid-Cycle Request': 'Mid-Cycle',
      'Final Bill': 'Final',
    };

    return emailTypeMap[emailType] || emailType;
  };

  // Filter button text colors (matching Juniper transactions)
  const getFilterButtonTextColor = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'paid':
        return 'text-green-900 dark:text-green-900';
      case 'partial payment':
        return 'text-blue-900 dark:text-blue-900';
      case 'pending_payment':
        return 'text-lime-900 dark:text-lime-900';
      case 'awaiting_payment':
        return 'text-yellow-900 dark:text-yellow-900';
      case 'awaiting_approval':
      case 'draft':
        return 'text-yellow-900 dark:text-yellow-900';
      case 'not_created':
        return 'text-xtnd-dark-600 dark:text-xtnd-dark-800';
      case 'void':
      case 'deleted':
        return 'text-red-900 dark:text-red-900';
      default:
        return 'text-xtnd-dark-800 dark:text-xtnd-dark-800';
    }
  };

  // Juniper Status badge colors (consistent with Juniper Transactions page)
  const getJuniperStatusColor = (status: string): string => {
    const baseClass = 'inline-flex px-2 py-1 text-xs font-semibold rounded-full';

    switch (status.toLowerCase()) {
      case 'completed':
      case 'email sent':
      case 'archived':
        return `${baseClass} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`;
      case 'processing':
      case 'pdf ready':
        return `${baseClass} bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200`;
      case 'trust pending approval':
      case 'trust request approved':
      case 'email held':
        return `${baseClass} bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200`;
      case 'failed':
      case 'trust request deleted':
      case 'trust request voided':
      case 'operational bill voided':
      case 'operational bill deleted':
        return `${baseClass} bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200`;
      default:
        return `${baseClass} bg-xtnd-dark-50 text-xtnd-dark-700 dark:bg-xtnd-dark-800 dark:text-xtnd-dark-100`;
    }
  };

  // TRR Status badge colors (consistent with Juniper Transactions page)
  const getTrustRequestStatusColor = (status: string): string => {
    const baseClass = 'inline-flex px-2 py-1 text-xs font-semibold rounded-full';

    switch (status.toLowerCase()) {
      case 'paid':
      case 'not required':
        return `${baseClass} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`;
      case 'partial payment':
        return `${baseClass} bg-blue-100 text-blue-900 dark:bg-blue-900 dark:text-blue-200`;
      case 'pending_payment':
        return `${baseClass} bg-lime-100 text-lime-800 dark:bg-lime-900 dark:text-lime-200`;
      case 'approved':
      case 'sent':
        return `${baseClass} bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200`;
      case 'awaiting payment':
        return `${baseClass} bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200`;
      case 'draft':
      case 'awaiting approval':
        return `${baseClass} bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200`;
      case 'not created':
        return `${baseClass} bg-xtnd-dark-50 text-xtnd-dark-500 dark:bg-xtnd-dark-700 dark:text-xtnd-dark-200`;
      case 'cancelled':
      case 'void':
      case 'voided':
      case 'deleted':
        return `${baseClass} bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200`;
      default:
        return `${baseClass} bg-xtnd-dark-50 text-xtnd-dark-700 dark:bg-xtnd-dark-800 dark:text-xtnd-dark-100`;
    }
  };

  const handleSelectAll = () => {
    if (selectedTransactions.size === transactions.length && transactions.length > 0) {
      setSelectedTransactions(new Set());
    } else {
      setSelectedTransactions(new Set(transactions.map(t => t.juniperTRRId)));
    }
  };

  const handleToggleSelection = (juniperTRRId: string) => {
    const newSelection = new Set(selectedTransactions);
    if (newSelection.has(juniperTRRId)) {
      newSelection.delete(juniperTRRId);
    } else {
      newSelection.add(juniperTRRId);
    }
    setSelectedTransactions(newSelection);
  };

  return (
    <div className="space-y-6">
      {/* Collapsible Filters Section */}
      <Card variant="default" className="w-full">
        <CardHeader className="pb-3">
          <button
            onClick={() => setIsFiltersCollapsed(!isFiltersCollapsed)}
            className="w-full flex items-center justify-between group"
            aria-expanded={!isFiltersCollapsed}
          >
            <div className="flex items-center space-x-3">
              <Icon name="HiFilter" className="h-5 w-5 text-xtnd-dark dark:text-xtnd-light" />
              <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">
                Filters
              </h3>
              <span className="text-sm text-xtnd-dark-600 dark:text-xtnd-dark-400">
                • {getDateRangeSummary()} • {getSelectedFiltersSummary()}
              </span>
            </div>
            <Icon
              name={isFiltersCollapsed ? 'HiChevronDown' : 'HiChevronUp'}
              className="h-5 w-5 text-xtnd-dark-400 dark:text-xtnd-dark-500 transition-transform duration-200"
            />
          </button>
        </CardHeader>

        {!isFiltersCollapsed && (
          <CardBody className="pt-0">
            {/* Responsive Filter Cards Container */}
            <div className="grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 gap-4">
              {/* Date Range Selector */}
              <Card variant="default" className="w-full">
                <CardBody>
                  <div className="flex flex-col gap-4">
                    <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">
                      Date Range
                    </h3>
                    <DatePicker
                      mode="range"
                      label=""
                      value={{
                        startDate: startDate ? new Date(startDate) : null,
                        endDate: endDate ? new Date(endDate) : null,
                      }}
                      onChange={range => {
                        // Only update when both dates are selected
                        if (range.startDate && range.endDate) {
                          setStartDate(range.startDate.toISOString().split('T')[0]);
                          setEndDate(range.endDate.toISOString().split('T')[0]);
                        }
                      }}
                      format={DateFormat.US}
                    />
                  </div>
                </CardBody>
              </Card>

              {/* Bill Status Filters */}
              <Card variant="default" className="w-full">
                <CardBody>
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">
                      Bill Status
                    </h3>

                    {/* Trust Request Status Filters */}
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium text-green-700 dark:text-green-300 flex items-center gap-2">
                        <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                        Trust Request Status
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {[
                          { value: 'not_created', label: 'Not Created', color: 'bg-xtnd-dark-50' },
                          { value: 'draft', label: 'Draft', color: 'bg-yellow-100' },
                          {
                            value: 'awaiting_approval',
                            label: 'Awaiting Approval',
                            color: 'bg-yellow-100',
                          },
                          {
                            value: 'awaiting_payment',
                            label: 'Awaiting Payment',
                            color: 'bg-yellow-100',
                          },
                          {
                            value: 'partial_payment',
                            label: 'Partial Payment',
                            color: 'bg-blue-100',
                          },
                          {
                            value: 'pending_payment',
                            label: 'Pending Payment',
                            color: 'bg-lime-100',
                          },
                          { value: 'paid', label: 'Paid', color: 'bg-green-100' },
                          { value: 'void', label: 'Void', color: 'bg-red-100' },
                          { value: 'deleted', label: 'Deleted', color: 'bg-red-100' },
                        ].map(status => (
                          <button
                            key={status.value}
                            onClick={() => {
                              if (selectedStatuses.includes(status.value)) {
                                setSelectedStatuses(selectedStatuses.filter(s => s !== status.value));
                              } else {
                                setSelectedStatuses([...selectedStatuses, status.value]);
                              }
                            }}
                            className={`
                            px-3 py-1.5 text-xs font-medium rounded-full transition-all duration-200
                            ${
                              selectedStatuses.includes(status.value)
                                ? `${status.color} ${getFilterButtonTextColor(status.value)} ring-2 ring-offset-1 ring-${status.color.split('-')[1]}-300`
                                : `bg-xtnd-light dark:bg-xtnd-dark-700 text-xtnd-dark dark:text-xtnd-light hover:bg-xtnd-blue hover:text-white`
                            }
                          `}
                          >
                            {status.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Clear Sub-Filters */}
                    {(selectedStatuses.length > 0) && (
                      <div className="flex justify-end">
                        <button
                          onClick={() => {
                            setSelectedStatuses([]);
                          }}
                          className="text-sm text-xtnd-dark-600 dark:text-xtnd-dark-400 hover:text-red-500 transition-colors duration-200"
                        >
                          Clear all status filters
                        </button>
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card>

              {/* Created By Filters */}
              <Card variant="default" className="w-full">
                <CardBody>
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">
                      Created By
                    </h3>

                    {/* User Filters */}
                    <div className="space-y-2">
                      <div className="max-h-60 overflow-y-auto space-y-1">
                        {isLoadingUsers ? (
                          <div className="flex items-center justify-center py-4">
                            <Icon name="HiArrowPath" className="h-4 w-4 animate-spin text-xtnd-blue" />
                            <span className="ml-2 text-sm text-xtnd-dark-400 dark:text-xtnd-dark-300">Loading users...</span>
                          </div>
                        ) : userNames && Object.keys(userNames).length > 0 ? (
                          Object.entries(userNames)
                            .sort(([, a], [, b]) => a.localeCompare(b))
                            .map(([userId, userName]) => (
                              <button
                                key={userId}
                                onClick={() => {
                                  setSelectedCreatedBy(selectedCreatedBy === userId ? '' : userId);
                                }}
                                className={`
                                w-full text-left px-3 py-2 text-sm rounded-md transition-all duration-200
                                ${
                                  selectedCreatedBy === userId
                                    ? 'bg-xtnd-blue text-white shadow-sm'
                                    : 'text-xtnd-dark dark:text-xtnd-light hover:bg-xtnd-light dark:hover:bg-xtnd-dark-700'
                                }
                              `}
                              >
                                {userName}
                              </button>
                            ))
                        ) : (
                          <div className="text-sm text-xtnd-dark-400 dark:text-xtnd-dark-300 py-2">
                            No users found
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Clear Created By Filter */}
                    {selectedCreatedBy && (
                      <div className="flex justify-end">
                        <button
                          onClick={() => {
                            setSelectedCreatedBy('');
                          }}
                          className="text-sm text-xtnd-dark-600 dark:text-xtnd-dark-400 hover:text-red-500 transition-colors duration-200"
                        >
                          Clear created by filter
                        </button>
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card>
            </div>
          </CardBody>
        )}
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card variant="outlined" padding="md">
          <CardBody>
            <div className="text-sm text-xtnd-dark-600 dark:text-xtnd-light-400">Total Requests</div>
            <div className="text-2xl font-bold text-xtnd-dark dark:text-xtnd-light">{summary.totalRequests}</div>
          </CardBody>
        </Card>
        <Card variant="outlined" padding="md">
          <CardBody>
            <div className="text-sm text-xtnd-dark-600 dark:text-xtnd-light-400">Total Amount</div>
            <div className="text-2xl font-bold text-xtnd-dark dark:text-xtnd-light">{formatCurrency(summary.totalAmount)}</div>
          </CardBody>
        </Card>
        <Card variant="outlined" padding="md">
          <CardBody>
            <div className="text-sm text-xtnd-dark-600 dark:text-xtnd-light-400">Completed</div>
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">{summary.byStatus.completed || 0}</div>
          </CardBody>
        </Card>
        <Card variant="outlined" padding="md">
          <CardBody>
            <div className="text-sm text-xtnd-dark-600 dark:text-xtnd-light-400">Pending</div>
            <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{summary.byStatus.pending || 0}</div>
          </CardBody>
        </Card>
      </div>

      {/* Trust Request Transactions Table */}
      <div id="trust-request-transactions-table-container" className="w-full max-w-full">
        <Card id="trust-request-transactions-table-card" variant="default" className="w-full shadow-sm">
          <CardHeader className="border-b border-xtnd-dark-100 dark:border-xtnd-dark-600 bg-white dark:bg-xtnd-dark-800">
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center space-x-4">
                <h2 className="text-xl font-semibold text-xtnd-dark dark:text-xtnd-light">
                  Trust Request Transactions
                </h2>
                {selectedTransactions.size > 0 && (
                  <div className="flex items-center space-x-2 px-3 py-1.5 bg-xtnd-blue-50 dark:bg-xtnd-blue-900/20 rounded-full">
                    <span className="text-sm font-medium text-xtnd-blue dark:text-xtnd-blue-300">
                      {selectedTransactions.size} selected
                    </span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {hasSelectedTransactions && (
                  <div className="flex flex-wrap items-center gap-2 mr-2">
                    {releaseEligibleTransactions.length > 0 && (
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={handleBulkRelease}
                        disabled={bulkReleaseMutation.isPending}
                        className="flex items-center space-x-1"
                      >
                        <Icon name="HiMail" className="h-4 w-4" />
                        <span>Release {releaseEligibleTransactions.length}</span>
                      </Button>
                    )}
                    {markCompletedEligibleTransactions.length > 0 && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleBulkMarkCompleted}
                        disabled={bulkMarkCompletedMutation.isPending}
                        className="flex items-center space-x-1"
                        title="Mark as completed without sending email"
                      >
                        <Icon name="HiCheckCircle" className="h-4 w-4" />
                        <span>Mark Completed {markCompletedEligibleTransactions.length}</span>
                      </Button>
                    )}
                    {resendEligibleTransactions.length > 0 && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleBulkResend}
                        disabled={bulkResendMutation.isPending}
                        className="flex items-center space-x-1"
                      >
                        <Icon name="HiMail" className="h-4 w-4" />
                        <span>Resend {resendEligibleTransactions.length}</span>
                      </Button>
                    )}
                    {regenerateEligibleTransactions.length > 0 && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleBulkRegenerate}
                        disabled={bulkRegenerateMutation.isPending}
                        className="flex items-center space-x-1"
                        title="Regenerate PDFs for Email Held requests"
                      >
                        <Icon name="HiRefresh" className="h-4 w-4" />
                        <span>Regenerate {regenerateEligibleTransactions.length}</span>
                      </Button>
                    )}
                    {refreshEligibleTransactions.length > 0 && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleBulkRefresh}
                        disabled={bulkRefreshMutation.isPending}
                        className="flex items-center space-x-1"
                      >
                        <Icon name="HiArrowPath" className="h-4 w-4" />
                        <span>Refresh {refreshEligibleTransactions.length}</span>
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={openBulkSimulateModal}
                      disabled={simulateEligibleTransactions.length === 0 || bulkSimulateMutation.isPending}
                      className="flex items-center space-x-1"
                      title={
                        simulateEligibleTransactions.length === 0
                          ? 'Select transactions with trust request numbers to retry last events'
                          : 'Retry last events for selected transactions'
                      }
                    >
                      <Icon name="HiLightningBolt" className="h-4 w-4" />
                      <span>
                        Retry Last Event(s){' '}
                        {simulateEligibleTransactions.length > 0
                          ? `(${simulateEligibleTransactions.length})`
                          : ''}
                      </span>
                    </Button>
                  </div>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsColumnModalOpen(true)}
                  className="flex items-center space-x-2 hover:bg-xtnd-dark-50 dark:hover:bg-xtnd-dark-600"
                  title="Edit columns"
                >
                  <Icon name="HiViewGrid" className="h-4 w-4" />
                  <span className="hidden sm:inline">Edit Columns</span>
                  <span className="inline-flex items-center justify-center text-xs bg-xtnd-dark-50 dark:bg-xtnd-dark-600 text-xtnd-dark-500 dark:text-xtnd-dark-200 px-1.5 py-0.5 rounded min-w-[2rem]">
                    {getVisibleColumnCount()}/{getTotalColumnCount()}
                  </span>
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardBody className="p-0">
            {activeBulkJobs.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 px-4 pt-3">
                {activeBulkJobs.map((jobId) => (
                  <BulkJobProgressTracker
                    key={jobId}
                    jobId={jobId}
                    endpoint="juniper-trr-transactions"
                    onDone={removeBulkJob}
                  />
                ))}
              </div>
            )}
            <div className="overflow-x-auto overflow-y-auto relative trust-request-transactions-scroll max-h-[calc(100vh-300px)] min-h-[700px]">
              <table className="min-w-full divide-y divide-xtnd-dark-100 dark:divide-xtnd-dark-600 relative">
                <thead className="bg-white dark:bg-xtnd-dark-700 sticky top-0 z-30">
                  {/* Section Header Row */}
                  <tr className="border-b-2 border-xtnd-dark-200 dark:border-xtnd-dark-500">
                    <th className="px-4 py-3 w-12 bg-xtnd-dark-50 dark:bg-xtnd-dark-600"></th>
                    <th
                      colSpan={getHeaderSpans().basicInfo}
                      className="px-6 py-3 text-xs font-bold text-xtnd-dark-600 dark:text-xtnd-dark-100 uppercase tracking-wider text-left bg-xtnd-dark-50 dark:bg-xtnd-dark-600 border-r-2 border-xtnd-dark-200 dark:border-xtnd-dark-500"
                    >
                      Basic Information
                    </th>
                    <th
                      colSpan={getHeaderSpans().trustRequestBill}
                      className="px-6 py-3 text-xs font-bold text-green-700 dark:text-green-300 uppercase tracking-wider text-left bg-green-100 dark:bg-green-900/30 border-r-2 border-xtnd-dark-200 dark:border-xtnd-dark-500"
                    >
                      Trust Request Bill
                    </th>
                    <th
                      className="py-3 px-3 text-xs font-bold text-xtnd-dark-600 dark:text-xtnd-dark-100 uppercase tracking-wider text-center bg-xtnd-dark-50 dark:bg-xtnd-dark-600 w-16 sticky right-0 z-40 shadow-[-4px_0_6px_-1px_rgba(0,0,0,0.1)]"
                    >
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>

                  {/* Column Headers Row */}
                  <tr className="bg-white dark:bg-xtnd-dark-700 border-b border-xtnd-dark-100 dark:border-xtnd-dark-600">
                    <th className="px-4 py-3 w-12 bg-xtnd-dark-50 dark:bg-xtnd-dark-700/50">
                      <div className="flex items-center justify-center space-x-2">
                        <input
                          type="checkbox"
                          checked={selectedTransactions.size === transactions.length && transactions.length > 0}
                          onChange={handleSelectAll}
                          className="w-4 h-4 text-xtnd-blue bg-xtnd-dark-50 border-xtnd-dark-200 rounded focus:ring-xtnd-blue"
                        />
                        <button
                          onClick={() => {
                            refetchTransactions();
                          }}
                          disabled={isFetchingTransactions}
                          className="p-1 text-xtnd-dark-300 hover:text-xtnd-blue disabled:opacity-50 disabled:cursor-not-allowed transition-colors rounded"
                          title="Refresh transactions"
                        >
                          <Icon name="HiRefresh" className={`h-4 w-4 ${isFetchingTransactions ? 'animate-spin' : ''}`} />
                        </button>
                      </div>
                    </th>
                    {/* Always visible columns */}
                    <th className="px-6 py-3 text-left text-xs font-medium text-xtnd-dark-500 dark:text-xtnd-dark-300 uppercase tracking-wider bg-xtnd-dark-50/50 dark:bg-xtnd-dark-700/30">
                      Matter
                    </th>
                    {visibleColumns.client && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-xtnd-dark-500 dark:text-xtnd-dark-300 uppercase tracking-wider bg-xtnd-dark-50/50 dark:bg-xtnd-dark-700/30 min-w-[200px]">
                        Client
                      </th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-xtnd-dark-500 dark:text-xtnd-dark-300 uppercase tracking-wider bg-xtnd-dark-50/50 dark:bg-xtnd-dark-700/30 min-w-[180px]">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-xtnd-dark-500 dark:text-xtnd-dark-300 uppercase tracking-wider bg-xtnd-dark-50/50 dark:bg-xtnd-dark-700/30 min-w-[150px]">
                      Last Sent
                    </th>
                    {visibleColumns.createdAt && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-xtnd-dark-500 dark:text-xtnd-dark-300 uppercase tracking-wider bg-xtnd-dark-50/50 dark:bg-xtnd-dark-700/30 min-w-[150px]">
                        Created
                      </th>
                    )}
                    {visibleColumns.createdBy && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-xtnd-dark-500 dark:text-xtnd-dark-300 uppercase tracking-wider bg-xtnd-dark-50/50 dark:bg-xtnd-dark-700/30 min-w-[150px]">
                        Created By
                      </th>
                    )}
                    {visibleColumns.paymentLink && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-xtnd-dark-500 dark:text-xtnd-dark-300 uppercase tracking-wider bg-xtnd-dark-50/50 dark:bg-xtnd-dark-700/30 border-r-2 border-xtnd-dark-100 dark:border-xtnd-dark-600 min-w-[200px]">
                        Payment Link
                      </th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-green-600 dark:text-green-400 uppercase tracking-wider bg-green-50/70 dark:bg-green-900/20">
                      Number
                    </th>
                    {visibleColumns.trustRequestEmailType && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-green-600 dark:text-green-400 uppercase tracking-wider bg-green-50/70 dark:bg-green-900/20 min-w-[160px]">
                        Email Type
                      </th>
                    )}
                    {visibleColumns.billStatus && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-green-600 dark:text-green-400 uppercase tracking-wider bg-green-50/70 dark:bg-green-900/20">
                        Status
                      </th>
                    )}
                    {visibleColumns.issueAt && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-green-600 dark:text-green-400 uppercase tracking-wider bg-green-50/70 dark:bg-green-900/20 min-w-[120px]">
                        Issue At
                      </th>
                    )}
                    {visibleColumns.dueAt && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-green-600 dark:text-green-400 uppercase tracking-wider bg-green-50/70 dark:bg-green-900/20 min-w-[120px]">
                        Due At
                      </th>
                    )}
                    {visibleColumns.total && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-green-600 dark:text-green-400 uppercase tracking-wider bg-green-50/70 dark:bg-green-900/20 min-w-[120px]">
                        Total
                      </th>
                    )}
                    {visibleColumns.paid && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-green-600 dark:text-green-400 uppercase tracking-wider bg-green-50/70 dark:bg-green-900/20 min-w-[120px]">
                        Paid
                      </th>
                    )}
                    {visibleColumns.pending && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-green-600 dark:text-green-400 uppercase tracking-wider bg-green-50/70 dark:bg-green-900/20 min-w-[120px]">
                        Pending
                      </th>
                    )}
                    <th className="px-6 py-3 text-center text-xs font-medium text-xtnd-dark-500 dark:text-xtnd-dark-300 uppercase tracking-wider bg-xtnd-dark-50 dark:bg-xtnd-dark-700/50 w-16 sticky right-0 z-[120] shadow-[-4px_0_6px_-1px_rgba(0,0,0,0.1)]">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-xtnd-dark-700 divide-y divide-xtnd-dark-100 dark:divide-xtnd-dark-600">
                  {isLoadingTransactions ? (
                    <tr>
                      <td
                        colSpan={getHeaderSpans().total}
                        className="px-6 py-8 text-center text-xtnd-dark-500 dark:text-xtnd-dark-400"
                      >
                        <div className="flex flex-col items-center space-y-2">
                          <Icon name="HiArrowPath" size="xl" className="animate-spin opacity-50" />
                          <p>Loading transactions...</p>
                        </div>
                      </td>
                    </tr>
                  ) : transactions.length === 0 ? (
                    <tr>
                      <td
                        colSpan={getHeaderSpans().total}
                        className="px-6 py-8 text-center text-xtnd-dark-500 dark:text-xtnd-dark-400"
                      >
                        <div className="flex flex-col items-center space-y-2">
                          <Icon name="HiInformationCircle" size="xl" className="opacity-50" />
                          <h3 className="text-lg font-semibold">No trust requests found</h3>
                          <p>Try adjusting your filters to see more results.</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    transactions.map((transaction) => {
                      const processingLocked =
                        !!transaction.processingAction && transaction.processingState !== 'failed';
                      return (
                      <tr
                        key={transaction.juniperTRRId}
                        className={`border-b border-xtnd-dark-100 dark:border-xtnd-dark-600 hover:bg-xtnd-dark-50 dark:hover:bg-xtnd-dark-600 ${
                          selectedTransactions.has(transaction.juniperTRRId) ? 'bg-xtnd-blue/5' : ''
                        }`}
                      >
                        <td className="px-4 py-4">
                          <input
                            type="checkbox"
                            checked={selectedTransactions.has(transaction.juniperTRRId)}
                            onChange={() => handleToggleSelection(transaction.juniperTRRId)}
                            disabled={processingLocked}
                            className="h-4 w-4 text-xtnd-blue border-xtnd-dark-200 dark:border-xtnd-dark-500 rounded focus:ring-2 focus:ring-xtnd-blue cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                            title={processingLocked ? 'Locked — bulk action in flight' : undefined}
                          />
                        </td>
                        {/* Always visible columns */}
                        {/* Matter - clickable number */}
                        <td className="px-6 py-4 text-sm">
                          <button
                            onClick={() => {
                              window.open(`https://app.clio.com/matters/${transaction.matterId}`, 'clio-tab');
                            }}
                            className="flex items-center space-x-1 text-xtnd-blue hover:text-xtnd-blue-600 dark:text-xtnd-blue-300 dark:hover:text-xtnd-blue-200 hover:underline focus:outline-none focus:ring-2 focus:ring-xtnd-blue rounded transition-colors"
                          >
                            <span className="font-medium max-w-xs truncate">
                              {transaction.trustRequestBill?.matters?.[0]?.display_number || '-'}
                            </span>
                            <Icon name="HiExternalLink" className="h-3.5 w-3.5 flex-shrink-0" />
                          </button>
                        </td>
                        {/* Client - clickable */}
                        {visibleColumns.client && (
                          <td className="px-6 py-4 text-sm text-xtnd-dark-800 dark:text-xtnd-dark-50 min-w-[200px]">
                            {transaction.trustRequestBill?.client?.id ? (
                              <button
                                onClick={() => {
                                  window.open(`https://app.clio.com/contacts/${transaction.trustRequestBill?.client?.id}`, 'clio-tab');
                                }}
                                className="flex items-center space-x-1 text-xtnd-blue hover:text-xtnd-blue-600 dark:text-xtnd-blue-300 dark:hover:text-xtnd-blue-200 hover:underline focus:outline-none focus:ring-2 focus:ring-xtnd-blue rounded transition-colors"
                              >
                                <span className="font-medium">
                                  {transaction.trustRequestBill?.client?.name || 'Unknown Client'}
                                </span>
                                <Icon name="HiExternalLink" className="h-3.5 w-3.5 flex-shrink-0" />
                              </button>
                            ) : (
                              <span className="font-medium">
                                {transaction.trustRequestBill?.client?.name || 'Unknown Client'}
                              </span>
                            )}
                          </td>
                        )}
                        {/* Status */}
                        <td className="px-6 py-4 text-sm min-w-[180px]">
                          <div className="flex flex-col gap-1">
                            <span
                              className={`${getJuniperStatusColor(transaction.status)} whitespace-nowrap`}
                            >
                              {toTitleCase(transaction.status)}
                            </span>
                            <ProcessingActionBadge
                              action={transaction.processingAction}
                              state={transaction.processingState}
                              error={transaction.processingError}
                            />
                          </div>
                        </td>
                        {/* Last Sent */}
                        <td className="px-6 py-4 text-sm text-xtnd-dark-800 dark:text-xtnd-dark-50 min-w-[150px] responsive-datetime">
                          {transaction.lastSentAt ? (
                            <div className="flex flex-col sm:block">
                              <div className="font-medium">
                                {formatDate(new Date(transaction.lastSentAt), DateFormat.US)}
                              </div>
                              <div className="text-xs text-xtnd-dark-400 sm:hidden">
                                {new Date(transaction.lastSentAt).toLocaleTimeString('en-US', {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  hour12: true
                                })}
                              </div>
                            </div>
                          ) : (
                            'Not sent'
                          )}
                        </td>
                        {/* Created */}
                        {visibleColumns.createdAt && (
                          <td className="px-6 py-4 text-sm text-xtnd-dark-800 dark:text-xtnd-dark-50 min-w-[150px] responsive-datetime">
                            {formatDate(new Date(transaction.createdAt), DateFormat.US)}
                          </td>
                        )}
                        {/* Created By */}
                        {visibleColumns.createdBy && (
                          <td className="px-6 py-4 text-sm text-xtnd-dark-800 dark:text-xtnd-dark-50 min-w-[150px]">
                            {transaction.createdByUserName || userNames[transaction.createdByUserId] || '-'}
                          </td>
                        )}
                        {/* Payment Link */}
                        {visibleColumns.paymentLink && (
                          <td className="px-6 py-4 text-sm text-xtnd-dark-800 dark:text-xtnd-dark-50 min-w-[200px]">
                            {(() => {
                              const hasPaymentLink =
                                transaction.clioPaymentLinkUrl && transaction.clioPaymentLinkId;
                              const isPaid =
                                transaction.trustRequestBill?.state?.toLowerCase() === 'paid';

                              if (!hasPaymentLink) {
                                return (
                                  <span className="text-xtnd-dark-300 dark:text-xtnd-dark-400">
                                    No link
                                  </span>
                                );
                              }

                              return (
                                <div className="flex items-center space-x-2">
                                  <a
                                    href={transaction.clioPaymentLinkUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`hover:underline flex items-center space-x-1 ${
                                      isPaid
                                        ? 'text-xtnd-dark-300 dark:text-xtnd-dark-400 cursor-not-allowed opacity-60'
                                        : 'text-xtnd-blue hover:text-xtnd-blue-600 dark:text-xtnd-blue-300 dark:hover:text-xtnd-blue-200'
                                    }`}
                                    onClick={e => {
                                      if (isPaid) {
                                        e.preventDefault();
                                      }
                                    }}
                                    title={
                                      isPaid
                                        ? 'Payment link not active - transaction is paid'
                                        : 'Open payment link'
                                    }
                                  >
                                    <span className="truncate max-w-[120px]">
                                      {transaction.clioPaymentLinkId}
                                    </span>
                                    <HiExternalLink className="h-3.5 w-3.5 flex-shrink-0" />
                                  </a>
                                  {isPaid && (
                                    <HiLockClosed
                                      className="h-4 w-4 text-xtnd-dark-300 dark:text-xtnd-dark-400 flex-shrink-0"
                                      title="Link inactive - transaction is paid"
                                    />
                                  )}
                                </div>
                              );
                            })()}
                          </td>
                        )}
                        {/* Always visible Trust Request Bill Number */}
                        <td className="px-6 py-4 text-sm">
                          {transaction.trustRequestBill?.id && transaction.trustRequestBill?.number ? (
                            <button
                              onClick={() => {
                                window.open(`https://app.clio.com/bills/${transaction.trustRequestBill!.id}`, 'clio-tab');
                              }}
                              className="flex items-center space-x-1 text-xtnd-blue hover:text-xtnd-blue-600 dark:text-xtnd-blue-300 dark:hover:text-xtnd-blue-200 hover:underline focus:outline-none focus:ring-2 focus:ring-xtnd-blue rounded transition-colors"
                            >
                              <span className="font-medium">
                                {transaction.trustRequestBill!.number}
                              </span>
                              <Icon name="HiExternalLink" className="h-3.5 w-3.5 flex-shrink-0" />
                            </button>
                          ) : (
                            <span className="text-xtnd-dark-500 dark:text-xtnd-dark-300">
                              {transaction.trustRequestBill?.number || '-'}
                            </span>
                          )}
                        </td>
                        {/* Email Type */}
                        {visibleColumns.trustRequestEmailType && (
                          <td className="px-6 py-4 text-sm text-xtnd-dark dark:text-xtnd-light min-w-[160px]">
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-xtnd-light dark:bg-xtnd-dark-700 text-xtnd-dark dark:text-xtnd-light">
                              {formatEmailType(transaction.trustRequestEmailType)}
                            </span>
                          </td>
                        )}
                        {/* Status */}
                        {visibleColumns.billStatus && (
                          <td className="px-6 py-4 text-sm min-w-[180px]">
                            {transaction.trustRequestBill?.state ? (
                              <span
                                className={`${getTrustRequestStatusColor(formatRawStatus(transaction.trustRequestBill.state))} whitespace-nowrap`}
                              >
                                {transaction.trustRequestStateDescription || formatRawStatus(transaction.trustRequestBill.state)}
                              </span>
                            ) : (
                              <span className="text-xtnd-dark-400 dark:text-xtnd-dark-300">-</span>
                            )}
                          </td>
                        )}
                        {/* Issue At */}
                        {visibleColumns.issueAt && (
                          <td className="px-6 py-4 text-sm font-medium text-xtnd-dark-800 dark:text-xtnd-dark-50 min-w-[120px]">
                            {transaction.trustRequestBill?.issued_at ? (
                              <div className="flex flex-col sm:block">
                                <div className="font-medium">
                                  {formatDate(new Date(transaction.trustRequestBill.issued_at), DateFormat.US)}
                                </div>
                              </div>
                            ) : (
                              <span className="text-xtnd-dark-400 dark:text-xtnd-dark-300">-</span>
                            )}
                          </td>
                        )}
                        {/* Due At */}
                        {visibleColumns.dueAt && (
                          <td className="px-6 py-4 text-sm font-medium text-xtnd-dark-800 dark:text-xtnd-dark-50 min-w-[120px]">
                            {transaction.trustRequestBill?.due_at ? (
                              <div className="flex flex-col sm:block">
                                <div className="font-medium">
                                  {formatDate(new Date(transaction.trustRequestBill.due_at), DateFormat.US)}
                                </div>
                              </div>
                            ) : (
                              <span className="text-xtnd-dark-400 dark:text-xtnd-dark-300">-</span>
                            )}
                          </td>
                        )}
                        {/* Total */}
                        {visibleColumns.total && (
                          <td className="px-6 py-4 text-sm font-medium text-xtnd-dark-800 dark:text-xtnd-dark-50 min-w-[120px]">
                            {transaction.trustRequestBill?.total != null ? formatCurrency(transaction.trustRequestBill.total) : (transaction.amount != null ? formatCurrency(transaction.amount) : '-')}
                          </td>
                        )}
                        {/* Paid */}
                        {visibleColumns.paid && (
                          <td className="px-6 py-4 text-sm font-medium text-xtnd-dark-800 dark:text-xtnd-dark-50 min-w-[120px]">
                            {transaction.trustRequestBill?.paid != null ? formatCurrency(transaction.trustRequestBill.paid) : '-'}
                          </td>
                        )}
                        {/* Pending */}
                        {visibleColumns.pending && (
                          <td className="px-6 py-4 text-sm font-medium text-xtnd-dark-800 dark:text-xtnd-dark-50 min-w-[120px]">
                            {transaction.trustRequestBill?.pending != null ? formatCurrency(transaction.trustRequestBill.pending) : '-'}
                          </td>
                        )}
                        <td className="px-6 py-4 text-sm text-center w-16 sticky right-0 z-[120] bg-white dark:bg-xtnd-dark-700 shadow-[-4px_0_6px_-1px_rgba(0,0,0,0.1)]">
                          <div className="relative">
                            <button
                              onClick={(e) => {
                                const newState = actionsMenuOpen === transaction.juniperTRRId ? null : transaction.juniperTRRId;
                                setActionsMenuOpen(newState);
                                setMenuTriggerElement(newState ? e.currentTarget : null);
                              }}
                              className="p-1.5 hover:bg-xtnd-dark-50 dark:hover:bg-xtnd-dark-600 rounded-lg transition-colors"
                              title="More actions"
                            >
                            <Icon
                              name="HiDotsVertical"
                              className="h-5 w-5 text-xtnd-dark-400 dark:text-xtnd-dark-300"
                            />
                          </button>

                          {/* Dropdown Menu */}
                          <ActionsMenu
                            isOpen={actionsMenuOpen === transaction.juniperTRRId}
                            onClose={() => setActionsMenuOpen(null)}
                            triggerElement={menuTriggerElement}
                          >
                            {/* View PDF */}
                            {(() => {
                              const hasPdf = transaction.documents?.storedAssets?.pdfs?.invoice;
                              const statusLower = transaction.status?.toLowerCase() || '';
                              const isValidStatus =
                                statusLower === 'email held' ||
                                statusLower.includes('email_held') ||
                                statusLower === 'completed' ||
                                statusLower.includes('completed');
                              const canView = hasPdf && isValidStatus;

                              return (
                                <ActionsMenuItem
                                  icon="HiDownload"
                                  iconColor={canView ? 'text-green-600' : 'text-xtnd-dark-300'}
                                  label="View/Download PDF"
                                  disabled={!canView}
                                  title={
                                    !canView
                                      ? `PDF not available - Transaction must be 'email-held' or 'completed' and have PDF`
                                      : undefined
                                  }
                                  onClick={() => {
                                    if (canView) {
                                      // Open PDF viewer for TRR transaction (similar to Evergreen)
                                      const viewerUrl = `/juniper/pdf-viewer/${transaction.juniperTRRId}?type=invoice&isTrr=true`;
                                      window.open(viewerUrl, '_blank');
                                      setActionsMenuOpen(null);
                                    }
                                  }}
                                />
                              );
                            })()}

                            {/* Regenerate PDF */}
                            {(() => {
                              const isEmailHeld = transaction.status?.toLowerCase().includes('email held');
                              const canRegenerate = isEmailHeld;

                              return (
                                <ActionsMenuItem
                                  icon="HiRefresh"
                                  iconColor={canRegenerate ? 'text-orange-500' : 'text-xtnd-dark-300'}
                                  label="Regenerate PDF"
                                  disabled={!canRegenerate || regeneratePdfMutation.isPending || processingLocked}
                                  title={
                                    processingLocked
                                      ? 'Locked — bulk action in flight'
                                      : !canRegenerate
                                        ? `Regeneration not available - Transaction must be 'email held'`
                                        : 'Generate fresh PDF with new Clio Payment link'
                                  }
                                  onClick={() => {
                                    if (canRegenerate) {
                                      regeneratePdfMutation.mutate({ juniperId: transaction.juniperTRRId });
                                      setActionsMenuOpen(null);
                                    }
                                  }}
                                />
                              );
                            })()}

                            {/* Send Email */}
                            {(() => {
                              // Check if transaction is eligible for email sending
                              const statusLower = transaction.status?.toLowerCase() || '';

                              // Only allow for Email Held or Completed statuses (not Draft)
                              const isEligible = statusLower.includes('email held') || statusLower.includes('completed');

                              return (
                                <ActionsMenuItem
                                  icon="HiMail"
                                  iconColor={
                                    transaction.status?.toLowerCase().includes('email held')
                                      ? 'text-yellow-500'
                                      : transaction.status
                                          ?.toLowerCase()
                                          .includes('completed')
                                        ? 'text-green-500'
                                        : 'text-xtnd-dark-300'
                                  }
                                  label="Send Email"
                                  disabled={
                                    sendEmailMutation.isPending ||
                                    releaseEmailMutation.isPending ||
                                    resendEmailMutation.isPending ||
                                    !isEligible ||
                                    processingLocked
                                  }
                                  title={
                                    processingLocked
                                      ? 'Locked — bulk action in flight'
                                      : !isEligible
                                        ? `Cannot send email - Transaction must be Email Held or Completed (not Draft)`
                                        : transaction.status?.toLowerCase().includes('email held')
                                          ? 'Release held email'
                                          : transaction.status?.toLowerCase().includes('completed')
                                            ? 'Resend email'
                                            : 'Send or resend email'
                                  }
                                  onClick={() => {
                                    openConfirmForTransaction(transaction);
                                    setActionsMenuOpen(null);
                                  }}
                                />
                              );
                            })()}

                            {/* Mark Completed */}
                            {transaction.status?.toLowerCase().includes('email held') && (
                              <ActionsMenuItem
                                icon="HiCheckCircle"
                                iconColor="text-green-500"
                                label="Mark Completed"
                                disabled={markCompletedMutation.isPending || processingLocked}
                                title={processingLocked ? 'Locked — bulk action in flight' : 'Mark as completed without sending email'}
                                onClick={() => {
                                  setActionsMenuOpen(null);
                                  markCompletedMutation.mutate({ juniperId: transaction.juniperTRRId });
                                }}
                              />
                            )}

                            {/* Refresh Status */}
                            <ActionsMenuItem
                              icon="HiRefresh"
                              iconColor="text-blue-600"
                              label="Refresh Status"
                              onClick={() => {
                                // TODO: Implement refresh
                                showToast.info('Refresh coming soon');
                                setActionsMenuOpen(null);
                              }}
                            />
                          </ActionsMenu>
                          </div>
                        </td>
                      </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {transactionsData?.pagination && transactionsData.pagination.totalPages > 1 && (
              <div className="mt-6 flex items-center justify-between">
                <div className="text-sm text-xtnd-dark-600 dark:text-xtnd-light-400">
                  Page {currentPage} of {transactionsData.pagination.totalPages} ({transactionsData.pagination.total} total)
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    size="sm"
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setCurrentPage(prev => Math.min(transactionsData.pagination.totalPages, prev + 1))}
                    disabled={currentPage === transactionsData.pagination.totalPages}
                    size="sm"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardBody>
      </Card>

      {/* Column Visibility Modal */}
      <ColumnVisibilityModal
        isOpen={isColumnModalOpen}
        onClose={() => setIsColumnModalOpen(false)}
        visibleColumns={visibleColumns}
        onToggleColumn={handleToggleColumn}
        onToggleAllColumns={handleToggleAllColumns}
      />
      <BulkSimulateModal
        isOpen={isBulkSimulateModalOpen}
        onClose={() => setBulkSimulateModalOpen(false)}
        onSubmit={async (eventType) => {
          await handleBulkSimulate(eventType);
        }}
        selectedCount={simulateEligibleTransactions.length}
        isLoading={bulkSimulateMutation.isPending}
        billTypeLocked="trust"
      />

      {/* Email Send Confirmation Modal */}
      <EmailSendConfirmationModal
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setConfirmTarget(null);
          setConfirmBulkMode(null);
          setBulkEligibleIds([]);
          previewRecipients.reset();
        }}
        onConfirm={(emailTemplateOverrideId) => {
          if (confirmBulkMode) {
            const mutation = confirmBulkMode === 'release' ? bulkReleaseMutation : bulkResendMutation;
            mutation.mutate(
              { juniperTRRIds: bulkEligibleIds, emailTemplateOverrideId },
              {
                onSuccess: (resp: any) => {
                  setSelectedTransactions(new Set());
                  trackBulkJob(resp);
                },
              },
            );
            setConfirmOpen(false);
            setConfirmTarget(null);
            setConfirmBulkMode(null);
            setBulkEligibleIds([]);
          } else if (confirmTarget?.juniperTRRId) {
            const statusLower = confirmTarget.status?.toLowerCase() || '';
            const isEmailHeld = statusLower.includes('email held');
            const mutation = isEmailHeld ? releaseEmailMutation : resendEmailMutation;
            mutation.mutate(
              { juniperId: confirmTarget.juniperTRRId, emailTemplateOverrideId },
              {
                onSuccess: () => {
                  setConfirmOpen(false);
                  setConfirmTarget(null);
                },
              }
            );
          }
        }}
        title={
          confirmBulkMode
            ? `${confirmBulkMode === 'release' ? 'Release' : 'Resend'} ${bulkEligibleIds.length} Email${bulkEligibleIds.length === 1 ? '' : 's'}`
            : 'Confirm Email Send'
        }
        confirmLabel={
          confirmBulkMode
            ? `${confirmBulkMode === 'release' ? 'Release' : 'Resend'} ${bulkEligibleIds.length} Email${bulkEligibleIds.length === 1 ? '' : 's'}`
            : 'Send Email'
        }
        isPending={releaseEmailMutation.isPending || resendEmailMutation.isPending || sendEmailMutation.isPending || bulkReleaseMutation.isPending || bulkResendMutation.isPending}
        isLoading={previewRecipients.isLoading}
        toRecipients={previewRecipients.toRecipients}
        bccRecipients={previewRecipients.bccRecipients}
        actualRecipients={previewRecipients.actualRecipients}
        overrideActive={previewRecipients.overrideActive}
        billType="trust"
        isReminder={confirmTarget ? !confirmTarget.status?.toLowerCase().includes('email held') : false}
        bulkCount={confirmBulkMode ? bulkEligibleIds.length : undefined}
        onRefreshContacts={confirmTarget && !confirmBulkMode ? previewRecipients.refresh : undefined}
        isRefreshingContacts={previewRecipients.isRefreshing}
      />
    </div>
    </div>
  );
}

